import {
  FormControl,
  InputLabel,
  MenuItem,
  FormHelperText,
  Select,
  type SxProps,
  type Theme,
} from "@mui/material";
import {
  type Control,
  Controller,
  type FieldValues,
  type Path,
} from "react-hook-form";

interface Props<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label: string;
  options: { label: string; value: string }[];
  multiple?: boolean;
  disabled?: boolean;
  gutterBottom?: boolean;
  error?: string;
  onChange?: (value: string) => void;
  sx?: SxProps<Theme>;
}

export const SelectWithControl = <T extends FieldValues>({
  control,
  name,
  label,
  options,
  multiple,
  disabled,
  gutterBottom,
  error,
  onChange,
  sx,
}: Props<T>) => {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        return (
          <FormControl
            fullWidth
            error={!!fieldState.error || !!error}
            disabled={disabled}
            sx={{ mb: gutterBottom ? 6 : undefined, ...sx }}
          >
            <InputLabel id={name}>{label}</InputLabel>

            <Select
              {...field}
              labelId={name}
              label={label}
              fullWidth
              multiple={multiple}
              error={!!fieldState.error || !!error}
              disabled={disabled}
              onChange={(e) => {
                field.onChange(e);
                if (onChange) onChange(e.target.value);
              }}
            >
              {options.map((option, idx) => (
                <MenuItem key={`${option.value}-${idx}`} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>

            {(fieldState.error?.message || error) && (
              <FormHelperText>
                {fieldState.error?.message || error}
              </FormHelperText>
            )}
          </FormControl>
        );
      }}
    />
  );
};
